/**
 * Read a design token at runtime.
 *
 * Canvas and inline SVG take colour as a string, not as a CSS value, so they
 * cannot consume `var(--color-blue)` the way a stylesheet can. Without this
 * they end up holding a hex literal — which is how `DotField` and `BeamLines`
 * came to carry four brand colours that no longer tracked the theme.
 *
 * Tokens are declared on `:root` by theme.css, so one `getComputedStyle` read
 * resolves any of them. Values are cached: they cannot change without a
 * rebuild, and this gets called from a canvas draw loop.
 */
const cache = new Map<string, string>()

export function token(name: `--${string}`): string {
  const hit = cache.get(name)
  if (hit !== undefined) return hit

  const value =
    typeof window === 'undefined'
      ? ''
      : getComputedStyle(document.documentElement).getPropertyValue(name).trim()

  /*
   * No hex fallback on purpose. A fallback here would be a second copy of the
   * value living in a component, which is the thing this whole phase removes —
   * and it would hide a renamed token instead of surfacing it. `check:tokens`
   * catches an unresolvable token before it ships; this catches the rest.
   */
  if (!value && import.meta.env.DEV) {
    console.warn(`[token] ${name} does not resolve — check src/styles/theme.css`)
  }

  cache.set(name, value)
  return value
}
